import React from 'react';
import { NewsArticle } from './types';

interface NewsCardProps {
  article: NewsArticle;
}

const NewsCard: React.FC<NewsCardProps> = ({ article }) => {
  return (
    <div className="uk-card uk-card-default uk-card-hover uk-card-body uk-border-rounded" 
         style={{ 
           border: '1px solid #e5e5e5',
           boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
           transition: 'all 0.3s ease',
           height: '100%',
           background: 'white',
           display: 'flex',
           flexDirection: 'column',
           padding: '12px',
           width: '100%',
           boxSizing: 'border-box'
         }}
         onMouseEnter={(e) => {
           e.currentTarget.style.transform = 'translateY(-5px)';
           e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
         }}
         onMouseLeave={(e) => {
           e.currentTarget.style.transform = 'translateY(0)';
           e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
         }}>
      
      {/* Imagen de la noticia */}
      <div style={{ position: 'relative', marginBottom: '10px', flexShrink: 0 }}>
        <img 
          src={article.image} 
          alt={article.title}
          className="uk-border-rounded"
          style={{ 
            width: '100%', 
            height: '100px', 
            objectFit: 'cover'
          }}
        />
        {/* Badge de tipo de noticia */}
        <div style={{
          position: 'absolute',
          top: '6px',
          left: '6px',
          background: article.isTechnical 
            ? 'linear-gradient(135deg, #20D3C7, #17BCBA)' 
            : 'linear-gradient(135deg, #FF6B6B, #FF8E53)',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '8px',
          fontSize: '8px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
        }}>
          {article.isTechnical ? 'Técnica' : 'Color'}
        </div>
        {/* Tiempo de lectura */}
        <div style={{
          position: 'absolute',
          bottom: '6px',
          right: '6px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '2px 5px',
          borderRadius: '8px',
          fontSize: '8px',
          backdropFilter: 'blur(10px)'
        }}>
          <i className="fas fa-clock" style={{ marginRight: '2px' }}></i>
          {article.readTime} min
        </div>
      </div>

      {/* Contenido de la noticia */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ 
          fontSize: '15px',
          fontWeight: '600',
          color: '#333',
          marginBottom: '6px',
          lineHeight: '1.3',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {article.title}
        </h3>
        
        <p style={{ 
          fontSize: '12px',
          color: '#666',
          lineHeight: '1.3',
          marginBottom: '8px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1
        }}>
          {article.excerpt}
        </p>

        {/* Meta información compacta */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '10px',
          color: '#888',
          marginBottom: '6px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-user" style={{ marginRight: '3px' }}></i>
            {article.author}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-calendar" style={{ marginRight: '3px' }}></i>
            {new Date(article.publishedDate).toLocaleDateString('es-AR', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </div>
        </div>

        {/* Fuente compacta */}
        <div style={{ 
          fontSize: '10px',
          color: '#20D3C7',
          fontWeight: '600',
          marginBottom: '6px',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          <i className="fas fa-building" style={{ marginRight: '3px' }}></i>
          {article.url ? (
            <a 
              href={article.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: 'inherit',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
                e.currentTarget.style.transform = 'translateX(2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              {article.source}
              <i className="fas fa-external-link-alt" style={{ 
                marginLeft: '3px', 
                fontSize: '7px',
                opacity: '0.7'
              }}></i>
            </a>
          ) : (
            article.source
          )}
        </div>

        {/* Tags compactos */}
        <div style={{ marginBottom: '8px' }}>
          {article.tags.slice(0, 2).map((tag, tagIndex) => (
            <span key={tagIndex} style={{
              display: 'inline-block',
              background: '#f0f0f0',
              color: '#666',
              padding: '1px 6px',
              borderRadius: '8px',
              fontSize: '9px',
              marginRight: '4px',
              marginBottom: '3px'
            }}>
              #{tag}
            </span>
          ))}
        </div>

        {/* Botón de leer más compacto */}
        <div style={{ marginTop: 'auto' }}>
          {article.url ? (
            <a 
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="uk-button uk-button-primary uk-button-small"
              style={{ 
                background: 'linear-gradient(135deg, #20D3C7, #17BCBA)',
                border: 'none',
                borderRadius: '15px',
                padding: '6px 16px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(32, 211, 199, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-external-link-alt uk-margin-small-right" style={{ color: 'white' }}></i>
              <span style={{ color: 'white' }}>Leer completo</span>
            </a>
          ) : (
            <button className="uk-button uk-button-primary uk-button-small"
                    style={{ 
                      background: 'linear-gradient(135deg, #20D3C7, #17BCBA)',
                      border: 'none',
                      borderRadius: '15px',
                      padding: '6px 16px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s ease',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(32, 211, 199, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
              <i className="fas fa-arrow-right uk-margin-small-right"></i>
              Leer más
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsCard;
